import { SiteControlLayout } from '@scaleaq/sitecontrol-lib';
import { create } from 'zustand';
import { persist, StorageValue } from 'zustand/middleware';

import { StaticPageNames } from '@/constants';

import { storeNames } from './config/config.type.ts';

export interface LayoutStorageState {
  currentLayoutId: string;
  currentLayout: () => SiteControlLayout | undefined;
  getCurrentLayoutFromStorage: () => SiteControlLayout | undefined;
  storageLayouts: SiteControlLayout[];

  addLayoutToStorage: (layout: SiteControlLayout) => void;
  findStoredLayoutById: (id?: string) => SiteControlLayout | undefined;
  findStoredStaticLayoutByPageName: (staticName: StaticPageNames) => SiteControlLayout | undefined;
  getWidgetConfigProps: <T>(id: string) => T | undefined;
  getWidgetSiteId: (widgetId: string) => string | undefined;
  removeLayoutFromStorage: (id: string) => void;
  removeWidgetConfigPropsById: ({ widgetId }: { widgetId: string }) => void;
  setCurrentLayoutIdInStorage: (layoutId: string) => void;
  setStorageLayouts: (layout: SiteControlLayout[]) => void;
  updateLayoutInStorage: (layout: SiteControlLayout) => void;
  updateAllLayoutInStorage: (layouts: SiteControlLayout[]) => void;
  updateWidgetConfigProps: <T>({ widgetId, config }: { widgetId: string; config: T }) => void;
  updateWidgetSiteId: ({ widgetId, siteId }: { widgetId: string; siteId: string }) => void;
}

export const useLayoutStorage = create(
  persist<LayoutStorageState>(
    (set, get) => ({
      currentLayoutId: '',
      currentLayout: () => get().storageLayouts.find(layout => layout.id === get().currentLayoutId),
      storageLayouts: [],
      getCurrentLayoutFromStorage: () =>
        get().storageLayouts.find(layout => layout.id === get().currentLayoutId),
      setCurrentLayoutIdInStorage: (layoutId: string) => {
        set((state: LayoutStorageState) => ({ ...state, currentLayoutId: layoutId }));
      },

      setStorageLayouts: (layouts: SiteControlLayout[]) => {
        set((state: LayoutStorageState) => ({
          ...state,
          isDirty: true,
          storageLayouts: layouts.map((l: SiteControlLayout) => ({
            ...l,
            widgets: l.widgets.map(w => ({ ...w })),
          })),
        }));
      },

      findStoredLayoutById: (id?: string) => {
        if (!id) {
          return undefined;
        }
        return get().storageLayouts.find(layout => layout.id === id);
      },

      findStoredStaticLayoutByPageName: (staticName: StaticPageNames) =>
        get().storageLayouts.find(layout => layout.pageName === staticName),

      getWidgetConfigProps: <T>(id: string): T | undefined => {
        const layout = get().storageLayouts.find(siteControlLayout =>
          siteControlLayout.widgets.some(widget => widget.id === id)
        );

        const widget = layout?.widgets.find(widget => widget.id === id);

        return widget?.props as T | undefined;
      },

      getWidgetSiteId: (widgetId: string) => {
        const layout = get().storageLayouts.find(l => l.widgets.some(w => w.id === widgetId));
        return layout?.widgets.find(w => w.id === widgetId)?.siteId;
      },

      updateWidgetConfigProps: <T>({ widgetId, config }: { widgetId: string; config: T }) => {
        set(state => ({
          ...state,
          isDirty: true,
          storageLayouts: state.storageLayouts.map(layout => ({
            ...layout,
            widgets: layout.widgets.map(widget => {
              if (widget.id === widgetId) {
                const props = { ...widget.props, ...(config as object) };
                return { ...widget, props };
              } else {
                return widget;
              }
            }),
          })),
        }));
      },

      updateWidgetSiteId: ({ widgetId, siteId }: { widgetId: string; siteId: string }) => {
        set(state => ({
          ...state,
          isDirty: true,
          storageLayouts: state.storageLayouts.map(layout => ({
            ...layout,
            widgets: layout.widgets.map(widget =>
              widget.id === widgetId ? { ...widget, siteId } : widget
            ),
          })),
        }));
      },

      removeWidgetConfigPropsById: ({ widgetId }: { widgetId: string }) =>
        set((state: LayoutStorageState) => ({
          ...state,
          isDirty: true,
          storageLayouts: state.storageLayouts.map(layout => ({
            ...layout,
            widgets: layout.widgets.filter(widget => widget.id !== widgetId),
          })),
        })),

      updateLayoutInStorage: (layout: SiteControlLayout) => {
        set((state: LayoutStorageState) => ({
          ...state,
          storageLayouts: state.storageLayouts.map(l => (l.id === layout.id ? { ...layout } : l)),
        }));
      },

      updateAllLayoutInStorage: (layouts: SiteControlLayout[]) => {
        set((state: LayoutStorageState) => ({
          ...state,
          storageLayouts: layouts,
        }));
      },

      addLayoutToStorage: (layout: SiteControlLayout) => {
        set(state => {
          const exists = state.storageLayouts.some(l => l.id === layout.id);
          if (exists) return state;
          return {
            ...state,
            storageLayouts: [...state.storageLayouts, { ...layout }],
          };
        });
      },

      removeLayoutFromStorage: (id: string) =>
        set((state: LayoutStorageState) => ({
          ...state,
          storageLayouts: state.storageLayouts.filter(l => l.id !== id),
        })),
    }),
    {
      name: storeNames.layout,
      storage: {
        getItem: (name: string) => {
          const str = localStorage.getItem(name);
          const result = JSON.parse(str || '') as StorageValue<LayoutStorageState>;

          return {
            ...result,
            state: {
              ...result.state,
              storageLayouts: result?.state?.storageLayouts.map((w: SiteControlLayout) => ({
                ...w,
              })),
            },
          };
        },
        setItem: (name: string, value: StorageValue<LayoutStorageState>) =>
          localStorage.setItem(name, JSON.stringify(value)),
        removeItem: (name: string) => localStorage.removeItem(name),
      },
    }
  )
);
